import { isEmpty } from 'lodash';
import { matchSorter } from 'match-sorter';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { messages } from 'i18n';
import { SearchInputProps, Sorters } from 'utils';
import { ExtendedCarrier } from './types';
import { CarriersSortTypes } from './utils';

export const sorters: Sorters<ExtendedCarrier, CarriersSortTypes> = {
  [CarriersSortTypes.Updated]: {
    sorter: (a, b) => moment(b.updatedAt ?? 0).diff(moment(a.updatedAt ?? 0)),
    label: messages.carriersPage.sorter.updated,
  },
  [CarriersSortTypes.Asc]: {
    sorter: (a, b) => a.organizationName.localeCompare(b.organizationName),
    label: messages.carriersPage.sorter.asc,
  },
  [CarriersSortTypes.Desc]: {
    sorter: (a, b) => b.organizationName.localeCompare(a.organizationName),
    label: messages.carriersPage.sorter.desc,
  },
};

export interface UseCarriersFilterProps extends SearchInputProps {
  sortedAndFilteredItems: ExtendedCarrier[];
  sortType: CarriersSortTypes;
  setSortType: (value: CarriersSortTypes) => void;
}

export default function useCarriersFilter(
  carriers: ExtendedCarrier[],
  paginationReset: () => void,
  defaultSortType = CarriersSortTypes.Asc,
): UseCarriersFilterProps {
  const [sortType, setSortType] = useState<CarriersSortTypes>(defaultSortType);
  const [searchInput, setSearchInput] = useState<string>('');

  const filteredCarriers = useMemo(
    () =>
      [...carriers].sort(sorters[sortType].sorter).filter((carrier) => {
        if (searchInput.length < 2) {
          return true;
        }

        const searchTerms = [carrier.organizationName, ...carrier.domains.map((domain) => domain.domain)].filter(
          Boolean,
        );

        const isInSearchTerms = searchTerms.some((term) =>
          String(term).toLowerCase().includes(searchInput.toLowerCase()),
        );
        if (isInSearchTerms) {
          return true;
        }
        const matchSorterFilter = matchSorter([carrier], searchInput, { keys: ['organizationName'] });
        return !isEmpty(matchSorterFilter);
      }),
    [carriers, searchInput, sortType],
  );

  const setSearch = (value: string) => {
    paginationReset();
    setSearchInput(value);
  };

  return {
    search: searchInput,
    setSearch,
    sortType,
    setSortType,
    sortedAndFilteredItems: filteredCarriers,
  };
}
